import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import type { AccountRefDTO, Locale, MeDTO, UserDTO } from '@btw/shared';
import { currentAccount, qk, useMe } from '../api/hooks';
import { isApiError } from '../api/client';
import { I18nProvider, guessLocale } from '../i18n';
import { writeStorage } from '../lib/hooks';
import { usePrelaunch } from './prelaunch';
import { pathInfo } from '../features/site/paths';

interface SessionValue {
  me: MeDTO | null;
  user: UserDTO | null;
  account: AccountRefDTO | null;
  loading: boolean;
  /** Network/server failure while probing the session (not "logged out"). */
  error: unknown;
  locale: Locale;
  /** For logged-out screens (login/register) the language is local only. */
  setGuestLocale: (l: Locale) => void;
  isOwner: boolean;
  canAuthorize: boolean;
  readOnly: boolean;
  /** Logged in (e.g. via a magic link) but the second factor is still pending. */
  totpPending: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Apply theme and large-text on <html> so tokens switch globally (§3, §10.1). */
function useDocumentPrefs(locale: Locale, remembered: Locale, theme: UserDTO['theme'] | undefined, largeText: boolean | undefined) {
  // <html lang> is set by the i18n provider (UI language) and website pages (their own language) — not here.
  void locale;
  // Remember the chosen language, not the one a website address imposes (/en would otherwise overwrite Chinese).
  useEffect(() => {
    writeStorage('btw.locale', remembered);
  }, [remembered]);
  useEffect(() => {
    const root = document.documentElement;
    const th = theme ?? 'system';
    if (th === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', th);
    root.classList.toggle('large-text', !!largeText);
    writeStorage('btw.theme', th);
    writeStorage('btw.largeText', largeText ? '1' : '0');
  }, [theme, largeText]);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // Prelaunch: no session at all. Wait for /api/features before probing /auth/me (it 404s in prelaunch).
  const pre = usePrelaunch();
  const meQuery = useMe(!pre.prelaunch && !pre.loading);
  const [guestLocale, setGuestLocale] = useState<Locale>(guessLocale);
  const unauth = isApiError(meQuery.error, 'unauthenticated') || (isApiError(meQuery.error) && meQuery.error.status === 401);
  const me = meQuery.data ?? null;
  // A guest on a website page sees the language of its address (/prijzen Dutch, /en/prijzen English) and keeps it
  // for the pages without a language in the address (login, register). Exception: Chinese. The website has no Chinese
  // but the app does, so a Chinese guest keeps Chinese for login, registration and the new account (2026-10-28).
  const urlLang = pathInfo(useLocation().pathname).lang;
  useEffect(() => {
    if (urlLang && !me) setGuestLocale((g) => (g === 'zh' ? g : urlLang));
  }, [urlLang, me]);
  const locale = me?.user.locale ?? urlLang ?? guestLocale;
  useDocumentPrefs(locale, me?.user.locale ?? guestLocale, me?.user.theme, me?.user.largeText);

  const value = useMemo<SessionValue>(() => {
    const account = currentAccount(me ?? undefined);
    const role = account?.role;
    return {
      me,
      user: me?.user ?? null,
      account,
      loading: pre.loading || meQuery.isLoading,
      error: unauth ? null : meQuery.error,
      locale,
      setGuestLocale,
      isOwner: role === 'owner',
      canAuthorize: role === 'owner' || role === 'filer',
      // Sprint 3A (ZL-12): a cancelled plan stays usable until its paid period ends; billing then makes it read_only.
      readOnly: account?.subscriptionStatus === 'read_only',
      totpPending: isApiError(meQuery.error, 'totp_required'),
    };
  }, [me, meQuery.isLoading, meQuery.error, unauth, locale, pre.loading]);

  return (
    <SessionContext.Provider value={value}>
      <I18nProvider locale={locale}>{children}</I18nProvider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Clear the cached session (after logout or a 401) without a full reload. */
export function useClearSession() {
  const qc = useQueryClient();
  return () => {
    qc.setQueryData(qk.me, undefined);
    qc.removeQueries({ queryKey: qk.me });
  };
}
